import {
    AlertTriangle,
    CheckCircle2,
    CircleDashed,
    Clock,
    ExternalLink,
    Info,
    RefreshCw,
    X,
    XCircle
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { activationTrackerService } from '../services/api';
import './ActivationTracker.css';

const ACTIVATION_STATUS_LABELS = {
    registered: 'Registrato',
    onboarding_incomplete: 'Onboarding incompleto',
    contract_pending: 'Contratto in attesa',
    pending_soontip_approval: 'Pending approval',
    approved_setup_required: 'Setup richiesto',
    collection_channel_setup_required: 'Canale di incasso richiesto',
    workers_setup_required: 'Worker richiesti',
    ready_for_go_live: 'Pronto per go-live',
    live: 'Live',
    suspended_closed: 'Suspended/Closed',
    unknown: 'Non determinato',
};

const STEP_STATUS_LABELS = {
    not_started: 'Non iniziato',
    in_progress: 'In corso',
    completed: 'Completato',
    blocked: 'Bloccato',
    unknown: 'Non disponibile',
};

const OWNER_LABELS = {
    cliente: 'Cliente',
    soontip_admin: 'Soontip',
    tecnico: 'Tecnico',
    worker: 'Worker',
    sistema: 'Sistema',
};

const BLOCKER_TYPE_LABELS = {
    operational_blocker: 'Bloccante',
    missing_data: 'Dato mancante',
    warning: 'Da verificare',
    info: 'Info',
    none: 'OK',
};

const BLOCKER_TYPE_TITLES = {
    operational_blocker: 'Problema che impedisce realmente attivazione o go-live.',
    missing_data: 'Dato non trovato: su tenant già attivi può essere storico o non tracciato.',
    warning: 'Situazione da verificare, ma non necessariamente bloccante.',
    info: 'Informazione operativa.',
    none: 'Nessun blocco rilevato.',
};

const STATUS_EXPLANATIONS = {
    tenantStatus: 'Stato amministrativo del tenant nel sistema.',
    activationStatus: 'Stato operativo del percorso verso il go-live.',
    live: 'Live = cliente operativo secondo i criteri del tracker, non semplicemente tenant ACTIVE.',
};

function formatDate(value) {
    if (!value) return 'Non disponibile';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Non disponibile';
    return date.toLocaleDateString('it-IT', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    });
}

function formatDays(days, fallbackLabel) {
    if (fallbackLabel) return fallbackLabel;
    if (days === null || days === undefined) return 'N/D';
    if (days === 0) return 'Oggi';
    if (days === 1) return '1 giorno';
    return `${days} giorni`;
}

function formatDaysCompact(days, fallbackLabel) {
    if (days === null || days === undefined) return 'N/D';
    if (days === 0) return 'Oggi';
    if (days === 1) return '1 gg';
    return `${days} gg`;
}

function getDaysTone(days) {
    if (days === null || days === undefined) return 'unknown';
    if (days > 5) return 'critical';
    if (days >= 2) return 'warning';
    return 'normal';
}

function StepIcon({ status }) {
    if (status === 'completed') return <CheckCircle2 size={18} />;
    if (status === 'blocked') return <XCircle size={18} />;
    if (status === 'in_progress') return <Clock size={18} />;
    if (status === 'unknown') return <AlertTriangle size={18} />;
    return <CircleDashed size={18} />;
}

function StatusBadge({ status }) {
    return (
        <span className={`at-badge at-badge--${status || 'unknown'}`}>
            {ACTIVATION_STATUS_LABELS[status] || status || 'Non determinato'}
        </span>
    );
}

function InfoHint({ title }) {
    return (
        <span className="at-info-hint" title={title} aria-label={title}>
            <Info size={13} />
        </span>
    );
}

function SignalBadge({ type = 'none' }) {
    const normalized = type || 'none';
    return (
        <span className={`at-signal-badge at-signal-badge--${normalized}`} title={BLOCKER_TYPE_TITLES[normalized] || normalized}>
            {BLOCKER_TYPE_LABELS[normalized] || normalized}
        </span>
    );
}

function TenantDetailDrawer({ detail, loading, error, isOpen, onClose }) {
    useEffect(() => {
        if (!isOpen) return undefined;
        const handler = (event) => {
            if (event.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handler);
        return () => document.removeEventListener('keydown', handler);
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const content = (() => {
    if (loading) {
        return (
            <>
                <div className="at-drawer-header">
                    <h2 id="activation-tracker-drawer-title">Tracker attivazione</h2>
                    <button className="at-drawer-close" type="button" onClick={onClose} aria-label="Chiudi tracker">
                        <X size={18} />
                    </button>
                </div>
                <div className="at-detail-empty">Caricamento dettaglio...</div>
            </>
        );
    }

    if (error) {
        return (
            <>
                <div className="at-drawer-header">
                    <h2 id="activation-tracker-drawer-title">Tracker attivazione</h2>
                    <button className="at-drawer-close" type="button" onClick={onClose} aria-label="Chiudi tracker">
                        <X size={18} />
                    </button>
                </div>
                <div className="at-detail-error">{error}</div>
            </>
        );
    }

    if (!detail) {
        return (
            <>
                <div className="at-drawer-header">
                    <h2 id="activation-tracker-drawer-title">Tracker attivazione</h2>
                    <button className="at-drawer-close" type="button" onClick={onClose} aria-label="Chiudi tracker">
                        <X size={18} />
                    </button>
                </div>
                <div className="at-detail-empty">Seleziona un cliente per vedere la checklist di attivazione.</div>
            </>
        );
    }

    return (
        <>
            <div className="at-detail-header">
                <div>
                    <h2 id="activation-tracker-drawer-title">{detail.tenantName}</h2>
                    <div className="at-detail-meta">
                        <span>{detail.tenantStatus}</span>
                        <StatusBadge status={detail.activationStatus} />
                        <span>{detail.progressPercent}% completato</span>
                    </div>
                </div>
                {detail.nextActionTarget && (
                    <Link className="at-action-link" to={detail.nextActionTarget}>
                        {detail.nextActionLabel || 'Apri'} <ExternalLink size={14} />
                    </Link>
                )}
                <button className="at-drawer-close" type="button" onClick={onClose} aria-label="Chiudi tracker">
                    <X size={18} />
                </button>
            </div>

            {(detail.mainBlocker || detail.nextActionLabel) && (
                <div className={`at-blocker at-blocker--${detail.blockerType || detail.blockerSeverity || 'medium'}`}>
                    <AlertTriangle size={18} />
                    <div>
                        <div className="at-blocker-title-row">
                            <strong>{detail.mainBlocker || 'Nessun blocco operativo rilevato'}</strong>
                            <SignalBadge type={detail.blockerType} />
                        </div>
                        <span>{formatDays(detail.daysInCurrentStep, detail.daysInCurrentStepLabel)} nello step corrente</span>
                        <span>Prossima azione: {detail.nextActionLabel || 'Nessuna azione richiesta'}</span>
                    </div>
                </div>
            )}

            <div className="at-timeline">
                {detail.steps.map((step) => (
                    <div className={`at-step at-step--${step.status}`} key={step.key}>
                        <div className="at-step-icon">
                            <StepIcon status={step.status} />
                        </div>
                        <div className="at-step-body">
                            <div className="at-step-title-row">
                                <h3><span className="at-step-number">{step.stepNumber}</span>{step.label}</h3>
                                <div className="at-step-badges">
                                    <SignalBadge type={step.blockerType} />
                                    <span>{STEP_STATUS_LABELS[step.status] || step.status}</span>
                                </div>
                            </div>
                            <div className="at-step-meta">
                                <span>Responsabile: {step.ownerLabel || OWNER_LABELS[step.owner] || step.owner}</span>
                                {step.completedAt && <span>Completato: {formatDate(step.completedAt)}</span>}
                            </div>
                            {step.blockingReason && (
                                <p className="at-step-blocking">{step.blockingReason}</p>
                            )}
                            <div className="at-step-recommended">
                                Azione consigliata: {step.recommendedAction || 'Nessuna azione richiesta'}
                            </div>
                            {step.isActionAvailable && step.actionTarget ? (
                                <Link className="at-step-action" to={step.actionTarget}>
                                    {step.actionLabel || 'Apri'}
                                </Link>
                            ) : (
                                <span className="at-step-action at-step-action--disabled">Azione manuale richiesta</span>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </>
    );
    })();

    return (
        <>
            <div className="at-drawer-overlay" onClick={onClose} />
            <aside className="at-drawer" role="dialog" aria-modal="true" aria-labelledby="activation-tracker-drawer-title">
                <div className="at-drawer-scroll">
                    {content}
                </div>
            </aside>
        </>
    );
}

export default function ActivationTracker() {
    const [searchParams] = useSearchParams();
    const [items, setItems] = useState([]);
    const [summary, setSummary] = useState(null);
    const [selectedTenantId, setSelectedTenantId] = useState(null);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [detailLoading, setDetailLoading] = useState(false);
    const [error, setError] = useState('');
    const [detailError, setDetailError] = useState('');
    const [filters, setFilters] = useState({
        search: '',
        status: '',
        onlyBlocked: false,
        onlyPendingApproval: false,
    });
    const requestedTenantId = searchParams.get('tenantId');

    const queryFilters = useMemo(() => ({
        search: filters.search.trim(),
        status: filters.status,
        onlyBlocked: filters.onlyBlocked ? 'true' : '',
        onlyPendingApproval: filters.onlyPendingApproval ? 'true' : '',
    }), [filters]);

    const loadList = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const response = await activationTrackerService.getTenants(queryFilters);
            const data = response.data || {};
            const nextItems = data.items || [];
            setItems(nextItems);
            setSummary(data.summary || null);
            setSelectedTenantId((current) => {
                if (current && nextItems.some((item) => item.tenantId === current)) return current;
                return null;
            });
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'Errore durante il caricamento.');
            setItems([]);
            setSummary(null);
            setSelectedTenantId(null);
        } finally {
            setLoading(false);
        }
    }, [queryFilters]);

    useEffect(() => {
        loadList();
    }, [loadList]);

    useEffect(() => {
        if (!requestedTenantId) return;
        setSelectedTenantId(requestedTenantId);
        setIsDrawerOpen(true);
    }, [requestedTenantId]);

    useEffect(() => {
        if (!selectedTenantId || !isDrawerOpen) {
            setDetail(null);
            return;
        }

        let cancelled = false;
        async function loadDetail() {
            setDetailLoading(true);
            setDetailError('');
            try {
                const response = await activationTrackerService.getTenant(selectedTenantId);
                if (!cancelled) setDetail(response.data);
            } catch (err) {
                if (!cancelled) {
                    setDetailError(err.response?.data?.message || err.message || 'Errore durante il caricamento dettaglio.');
                    setDetail(null);
                }
            } finally {
                if (!cancelled) setDetailLoading(false);
            }
        }

        loadDetail();
        return () => { cancelled = true; };
    }, [selectedTenantId, isDrawerOpen]);

    const openTracker = useCallback((tenantId) => {
        setSelectedTenantId(tenantId);
        setIsDrawerOpen(true);
    }, []);

    const closeTracker = useCallback(() => {
        setIsDrawerOpen(false);
    }, []);

    const kpis = [
        { key: 'activation', label: 'Clienti in attivazione', value: summary?.activation ?? 0 },
        { key: 'blocked', label: 'Clienti bloccati', value: summary?.blocked ?? 0, tone: 'danger' },
        { key: 'pending', label: 'Pending approval', value: summary?.pendingApproval ?? 0, tone: 'warning' },
        { key: 'ready', label: 'Pronti go-live', value: summary?.readyForGoLive ?? 0, tone: 'success' },
        { key: 'live', label: 'Live', value: summary?.live ?? 0, tone: 'neutral', title: STATUS_EXPLANATIONS.live },
    ];

    return (
        <main className="activation-tracker-page">
            <header className="at-page-header">
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                        <h1 style={{ margin: 0 }}>Clienti in attivazione</h1>
                        <span
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                padding: '4px 8px',
                                borderRadius: 999,
                                border: '1px solid #fcd34d',
                                background: '#fffbeb',
                                color: '#b45309',
                                fontSize: '0.72rem',
                                fontWeight: 800,
                                letterSpacing: '0.04em',
                                textTransform: 'uppercase',
                            }}
                        >
                            Deprecated
                        </span>
                    </div>
                    <p>Pagina deprecated. Usa la vista Tenants per il tracker operativo e le azioni di attivazione.</p>
                </div>
                <button className="at-icon-button" onClick={loadList} disabled={loading} aria-label="Aggiorna dati">
                    <RefreshCw size={16} />
                </button>
            </header>

            <section className="at-kpi-grid" aria-label="KPI activation tracker">
                {kpis.map((kpi) => (
                    <div className={`at-kpi-card at-kpi-card--${kpi.tone || 'default'}`} key={kpi.key} title={kpi.title || ''}>
                        <span>{kpi.label}</span>
                        <strong>{kpi.value}</strong>
                    </div>
                ))}
            </section>

            <section className="at-status-help" aria-label="Definizioni stati">
                <span>Stato tenant <InfoHint title={STATUS_EXPLANATIONS.tenantStatus} /></span>
                <span>Stato attivazione <InfoHint title={STATUS_EXPLANATIONS.activationStatus} /></span>
                <span>Live <InfoHint title={STATUS_EXPLANATIONS.live} /></span>
            </section>

            <section className="at-filters">
                <div className="at-filter-group at-filter-group--search">
                    <label htmlFor="activation-search">Ricerca</label>
                    <input
                        id="activation-search"
                        type="search"
                        value={filters.search}
                        onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))}
                        placeholder="Nome cliente, stato o blocco"
                    />
                </div>
                <div className="at-filter-group">
                    <label htmlFor="activation-status">Stato attivazione</label>
                    <select
                        id="activation-status"
                        value={filters.status}
                        onChange={(event) => setFilters((prev) => ({ ...prev, status: event.target.value }))}
                    >
                        <option value="">Tutti</option>
                        {Object.entries(ACTIVATION_STATUS_LABELS).map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                        ))}
                    </select>
                </div>
                <label className="at-check-filter">
                    <input
                        type="checkbox"
                        checked={filters.onlyBlocked}
                        onChange={(event) => setFilters((prev) => ({ ...prev, onlyBlocked: event.target.checked }))}
                    />
                    Solo bloccati
                </label>
                <label className="at-check-filter">
                    <input
                        type="checkbox"
                        checked={filters.onlyPendingApproval}
                        onChange={(event) => setFilters((prev) => ({ ...prev, onlyPendingApproval: event.target.checked }))}
                    />
                    Pending approval
                </label>
            </section>

            {error && <div className="at-error">{error}</div>}

            <section className="at-table-panel">
                    {loading ? (
                        <div className="at-empty">Caricamento clienti in attivazione...</div>
                    ) : items.length === 0 ? (
                        <div className="at-empty">Nessun cliente corrisponde ai filtri selezionati.</div>
                    ) : (
                        <div className="at-table-scroll">
                            <table className="at-table">
                                <colgroup>
                                    <col className="at-col-client" />
                                    <col className="at-col-tenant" />
                                    <col className="at-col-status" />
                                    <col className="at-col-progress" />
                                    <col className="at-col-type" />
                                    <col className="at-col-blocker" />
                                    <col className="at-col-days" />
                                    <col className="at-col-next" />
                                    <col className="at-col-actions" />
                                </colgroup>
                                <thead>
                                    <tr>
                                        <th>Cliente</th>
                                        <th>Stato tenant <InfoHint title={STATUS_EXPLANATIONS.tenantStatus} /></th>
                                        <th>Stato attivazione <InfoHint title={STATUS_EXPLANATIONS.activationStatus} /></th>
                                        <th>Avanzamento</th>
                                        <th>Tipo</th>
                                        <th>Blocco principale</th>
                                        <th>Giorni fermo</th>
                                        <th>Prossima azione</th>
                                        <th>Azioni</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((item) => (
                                        <tr
                                            key={item.tenantId}
                                            className={selectedTenantId === item.tenantId ? 'selected' : ''}
                                            onClick={() => openTracker(item.tenantId)}
                                            aria-selected={selectedTenantId === item.tenantId && isDrawerOpen}
                                        >
                                            <td>
                                                <button className="at-row-button" type="button">
                                                    {item.tenantName}
                                                </button>
                                            </td>
                                            <td>{item.tenantStatus}</td>
                                            <td><StatusBadge status={item.activationStatus} /></td>
                                            <td>
                                                <div className="at-progress-cell">
                                                    <div className="at-progress-bar">
                                                        <span style={{ width: `${item.progressPercent}%` }} />
                                                    </div>
                                                    <span>{item.progressPercent}%</span>
                                                </div>
                                            </td>
                                            <td><SignalBadge type={item.blockerType} /></td>
                                            <td>
                                                <span
                                                    className={`at-blocker-text at-blocker-text--${item.blockerSeverity || 'low'}`}
                                                    title={item.mainBlocker || 'Nessun blocco operativo'}
                                                >
                                                    {item.mainBlocker || 'Nessun blocco operativo'}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={`at-days at-days--${getDaysTone(item.daysInCurrentStep)}`}>
                                                    {formatDaysCompact(item.daysInCurrentStep, item.daysInCurrentStepLabel)}
                                                </span>
                                            </td>
                                            <td>
                                                {item.nextActionTarget ? (
                                                    <Link
                                                        className="at-table-action"
                                                        to={item.nextActionTarget}
                                                        onClick={(event) => event.stopPropagation()}
                                                    >
                                                        {item.nextActionLabel || 'Apri'}
                                                    </Link>
                                                ) : (
                                                    <span className="at-muted">{item.nextActionLabel || 'Nessuna azione richiesta'}</span>
                                                )}
                                            </td>
                                            <td>
                                                <button
                                                    className="at-view-tracker-btn"
                                                    type="button"
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        openTracker(item.tenantId);
                                                    }}
                                                >
                                                    Vedi tracker
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
            </section>

            <TenantDetailDrawer
                detail={detail}
                loading={detailLoading}
                error={detailError}
                isOpen={isDrawerOpen}
                onClose={closeTracker}
            />
        </main>
    );
}
